import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LabelTemplate } from '@api/model/labelTemplate';
import { Store } from '@ngxs/store';
import { interval, lastValueFrom, Observable, of, timer } from 'rxjs';
import { filter, switchMap, takeWhile, tap } from 'rxjs/operators';
import { GetLabelTemplate, UpdateLabelTemplate } from '../../app/portal/master-data/label-templates/state/label-templates.actions';
import { LabelTemplateState } from '../portal/master-data/label-templates/state/label-templates.state';
import { ComponentBaseDirective } from '../shared/component.base';
import { ComponentBaseService } from '../shared/component.base.service';
import { ConfirmationDialogResult } from '../shared/components/confirmation-dialog/confirmation-dialog-result.enumeration';
import { ConfirmationDialogService } from '../shared/components/confirmation-dialog/confirmation-dialog.service';
import { getRandomId } from '../shared/extensions/string.extensions';
import { ICanDeactivate } from '../shared/services/deactivation-guard/can-deactivate.interface';

declare global {
  interface Window {
    loadSvgString: (svgContent: string) => void;
    svgCanvasToString: () => string;
    selectAllInCurrentLayer: () => void;
    ungroupSelectedElement: () => void;
    clearSelection: () => void;
  }
}

@Component({
  selector: 'app-svg-designer',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './svg-designer.component.html',
  styleUrl: './svg-designer.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class SvgDesignerComponent extends ComponentBaseDirective implements OnInit, AfterViewInit, OnDestroy, ICanDeactivate {
  private labelTemplate$: Observable<LabelTemplate> = this.store.select(LabelTemplateState.getLabelTemplate);

  private labelTemplateId: string;
  private previousElementId = '';
  private isSvgScriptLoaded: boolean;

  public activeLabelTemplate: LabelTemplate;
  public selectedElementId = '';
  public lastSavedSvgContent: string;
  public readonly defaultTheme = 'inverted';
  public readonly svgEditorScriptId = 'svgDesignerScript';
  public readonly svgEditorStyleId = 'svgDesignerStyle';

  @HostListener('window:keydown.control.s', ['$event'])
  public onControlSKeyPress(event: Event) {
    (event as KeyboardEvent).preventDefault();
    this.saveSvg();
  }

  constructor(
    public componentBaseService: ComponentBaseService,
    private renderer: Renderer2,
    public store: Store,
    private activatedRoute: ActivatedRoute,
    public router: Router,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    super(componentBaseService);
  }

  public async canDeactivate() {
    const hasChanges = this.lastSavedSvgContent != this.canvasSvgContent;

    if (!hasChanges) {
      return true;
    }

    const confirmationDialogResult = await (hasChanges ? this.confirmationDialogService.openUnsavedChangesConfirmationDialog() : this.confirmationDialogService.openDiscardChangesConfirmationDialog());

    switch (confirmationDialogResult) {
      case ConfirmationDialogResult.SaveChanges:
        return lastValueFrom(this.updateLabelTemplate());
      case ConfirmationDialogResult.DiscardChanges:
        return true;
    }

    return false;
  }

  public ngOnInit() {
    this.injectSvgEditorStyle();
    this.applyThemeClass(this.defaultTheme);
    this.addToMainLoader(GetLabelTemplate, UpdateLabelTemplate);
    this.subscribeLabelTemplate();
    this.initNotifications();
    this.addRouteParamsChanged(this.activatedRoute, false);
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
    this.removeSvgEditorScript();
    this.removeSvgEditorStyle();
    localStorage.removeItem('md-canvasContent');
    localStorage.removeItem('md-canvasTitle');
  }

  public routeParamsChanged(routerParams: Params) {
    this.labelTemplateId = routerParams['id'];

    if (this.labelTemplateId != null) {
      this.store.dispatch(new GetLabelTemplate(this.labelTemplateId));
    }
  }

  public ngAfterViewInit() {
    const svgRootElement = document.querySelector('#workarea');
    svgRootElement?.addEventListener('click', (e) => this.onElementClick(e));
  }

  public onElementClick(event: Event) {
    const target = event.target as HTMLElement;
    const idElement = document.getElementById('idInputElement') as HTMLInputElement;
    if (!target) {
      return;
    }

    if (target.id === 'svgroot' || target.id === 'idInputElement') {
      idElement.value = '';
      this.selectedElementId = '';
      this.previousElementId = '';
      return;
    }

    idElement.value = target.id;
    this.previousElementId = target.id;
  }

  public renameSelectedElement() {
    const element = document.getElementById(this.previousElementId);
    const idElement = document.getElementById('idInputElement') as HTMLInputElement;
    const existingElement = document.getElementById(idElement.value);

    if (idElement.value === this.previousElementId) {
      return;
    }

    if (!idElement.value) {
      this.notificationsService.error('svgDesigner.notifications.emptyId');
      return;
    }

    if (existingElement) {
      this.notificationsService.error('svgDesigner.notifications.duplicateElementId');
      return;
    }

    if (element) {
      this.previousElementId = this.selectedElementId;
      element.id = idElement.value;
    }
  }

  public async saveSvg() {
    await lastValueFrom(this.updateLabelTemplate());
  }

  public navigateToLabelTemplateDetails() {
    this.router.navigate(['/labeltemplates', this.labelTemplateId, 'details']);
  }

  public injectSvgEditorScript() {
    const id = getRandomId();
    this.injectScript(`../assets/svg-designer/all.js?id=${id}`);
  }

  public injectSvgEditorStyle() {
    this.injectStyle('../assets/svg-designer/all.min.css');
  }

  public removeSvgEditorScript() {
    const oldScript = document.querySelector(`script[id*="${this.svgEditorScriptId}"]`);
    if (oldScript) {
      oldScript.remove();
    }
  }

  public removeSvgEditorStyle() {
    const oldStyle = document.querySelector(`link[id*="${this.svgEditorStyleId}"]`);
    if (oldStyle) {
      oldStyle.remove();
    }
  }

  private initNotifications() {
    this.addSuccessNotification('labelTemplate.notifications.updateLabelTemplateSucceeded', UpdateLabelTemplate);

    this.addErrorNotification('labelTemplate.notifications.getLabelTemplateFailed', GetLabelTemplate);
    this.addErrorNotification('labelTemplate.notifications.updateLabelTemplateFailed', UpdateLabelTemplate);
  }

  private applyThemeClass(className: string) {
    this.renderer.addClass(document.body, className);
  }

  private injectScript(url: string) {
    this.removeSvgEditorScript();
    Object.assign(document.body.appendChild(document.createElement('script')), {
      src: url,
      id: this.svgEditorScriptId,
      type: 'text/javascript',
    });
  }

  private injectStyle(url: string) {
    this.removeSvgEditorStyle();
    Object.assign(document.body.appendChild(document.createElement('link')), {
      href: url,
      id: this.svgEditorStyleId,
      rel: 'stylesheet',
    });
  }

  private updateLabelTemplate() {
    this.isSvgScriptLoaded = true;
    const labelTemplate = this.createUpdatedLabelTemplate();
    return this.store.dispatch(new UpdateLabelTemplate(this.labelTemplateId, labelTemplate)).pipe(
      switchMap(() => {
        this.isSvgScriptLoaded = false;

        return of(true);
      })
    );
  }

  private createUpdatedLabelTemplate() {
    let templateName = (this.activeLabelTemplate.file as File).name;
    if (!templateName) {
      templateName = 'Blank.svg';
    }

    const blob = new Blob([this.canvasSvgContent], { type: 'image/svg+xml;charset=utf-8' });
    const file = new File([blob], templateName, { type: 'image/svg+xml' });
    this.activeLabelTemplate = { ...this.activeLabelTemplate, file };

    return this.activeLabelTemplate;
  }

  private get canvasSvgContent() {
    return typeof window.svgCanvasToString === 'function' ? window.svgCanvasToString() : '';
  }

  private setContentStringForSvgDesigner(fileData: { content: string; contentType: string }) {
    if (fileData?.content && fileData?.contentType == 'image/svg+xml') {
      const previousLabelTemplateSvgContent = atob(fileData.content);
      window.loadSvgString(previousLabelTemplateSvgContent);

      this.subscription.add(
        timer(500)
          .pipe(
            tap(() => {
              this.ungroupSelectedSvgElements();
              this.lastSavedSvgContent = this.canvasSvgContent.trim();
            })
          )
          .subscribe()
      );
    }
  }

  private ungroupSelectedSvgElements() {
    window.selectAllInCurrentLayer();
    window.ungroupSelectedElement();
    window.clearSelection();
  }

  private subscribeLabelTemplate() {
    this.subscription.add(
      this.labelTemplate$
        .pipe(
          filter((labelTemplate) => labelTemplate != null && labelTemplate.id == this.labelTemplateId),
          tap((labelTemplate) => {
            this.activeLabelTemplate = labelTemplate;

            if (!this.isSvgScriptLoaded) {
              this.injectSvgEditorScript();
            }
          }),
          switchMap(() => interval(500).pipe(takeWhile(() => typeof window?.loadSvgString != 'function', true))),
          filter(() => typeof window?.loadSvgString === 'function'),
          tap(() => {
            this.setContentStringForSvgDesigner(this.activeLabelTemplate.file);
          })
        )
        .subscribe()
    );
  }
}
